import type {
  TTourAccommodation,
  TTourFiltersState,
  TTourFiltersTourFormat,
} from '@/entities/filter/store/types/tour/state';
import type { LocationQuery } from 'vue-router';
import { DEFAULT_SORTING } from '@/entities/filter/store/const/config';
import {
  ACCOMMODATION_VALUE,
  ACTIVITY_LEVEL_VALUE,
  type ActivityLevelValue,
  COMFORT_VALUE,
  type ComfortValue,
} from '@/shared/types/basic';

export function getTourInitialFiltersState(): TTourFiltersState {
  return {
    date: {
      from: null,
      to: null,
    },
    persons: 1,
    tourFormat: [],
    toursActivities: null,
    price: [null, null],
    selected_rubric: null,
    tag__slugs: 'all',
    sorting: 'rank',
    duration: [null, null],
    comfort_type: [],
    accommodation_type: [],
    activity_level_category: [],
    childrenAllowed: null,
  };
}

function normalizeTagSlug(tag: string) {
  return tag.replace(/^\d+-/, '');
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function setTourInitialFilterState(queryParams: LocationQuery, tag: string) {
  const defaultParams = {
    date: {
      from: null,
      to: null,
    },
    persons: 1,
    tourFormat: [],
    toursActivities: null,
    price: [null, null],
    selected_rubric: null,
    tag__slugs: 'all',
    sorting: null,
    duration: [null, null],
    comfort_type: [],
    accommodation_type: [],
    activity_level_category: [],
    childrenAllowed: null,
  } as TTourFiltersState;

  if (queryParams.start_price || queryParams.end_price) {
    defaultParams.price = [
      queryParams.start_price ? Number(queryParams.start_price) : null,
      queryParams.end_price ? Number(queryParams.end_price) : null,
    ];
  }

  if (queryParams.start_date || queryParams.end_date || queryParams.persons_count) {
    defaultParams.date = {
      from: queryParams.start_date ? queryParams.start_date.toString() : null,
      to: queryParams.end_date ? queryParams.end_date.toString() : null,
    };
    defaultParams.persons = queryParams.persons_count ? Number(queryParams.persons_count) : 1;
  }

  if (queryParams.type) {
    const rawTypes = queryParams.type
      .toString()
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    const BARE_TO_TOUR: Record<string, TTourFiltersTourFormat[number]> = {
      private: 'tour_private',
      group: 'tour_group',
      minigroup: 'tour_minigroup',
    };

    const fromTourPrefix = rawTypes.filter(
      (v): v is TTourFiltersTourFormat[number] => v === 'tour_private' || v === 'tour_group' || v === 'tour_minigroup',
    );

    const fromBare = rawTypes
      .filter((v): v is keyof typeof BARE_TO_TOUR => v in BARE_TO_TOUR)
      .map((v) => BARE_TO_TOUR[v]);

    defaultParams.tourFormat = [...new Set([...fromTourPrefix, ...fromBare])];
  }

  if (queryParams.tours_activities) {
    defaultParams.toursActivities = queryParams.tours_activities
      ? queryParams.tours_activities.toString().split(',').filter(Boolean)
      : null;
  }

  if (queryParams.start_duration || queryParams.end_duration) {
    defaultParams.duration = [
      queryParams.start_duration ? Number(queryParams.start_duration) : null,
      queryParams.end_duration ? Number(queryParams.end_duration) : null,
    ];
  }

  if (queryParams.sorting) {
    defaultParams.sorting = queryParams.sorting as string;
  } else {
    defaultParams.sorting = DEFAULT_SORTING;
  }
  if (queryParams.comfort_type) {
    defaultParams.comfort_type = queryParams.comfort_type
      .toString()
      .split(',')
      .filter((time): time is ComfortValue => Object.values(COMFORT_VALUE).includes(time as ComfortValue));
  }
  if (queryParams.activity_level_category) {
    defaultParams.activity_level_category = queryParams.activity_level_category
      .toString()
      .split(',')
      .filter((time): time is ActivityLevelValue =>
        Object.values(ACTIVITY_LEVEL_VALUE).includes(time as ActivityLevelValue),
      );
  }

  const ALLOWED_ACCOMMODATION_ATOMS = new Set(
    Object.values(ACCOMMODATION_VALUE)
      .flatMap((v) => v.split(','))
      .map((s) => s.trim())
      .filter(Boolean),
  );

  if (queryParams.accommodation_type) {
    const atoms = queryParams.accommodation_type
      .toString()
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    defaultParams.accommodation_type = atoms.filter((a) => ALLOWED_ACCOMMODATION_ATOMS.has(a)) as TTourAccommodation;
  }

  if (queryParams.children_allowed) {
    defaultParams.childrenAllowed = true;
  }

  if (tag) {
    defaultParams.tag__slugs = normalizeTagSlug(tag);
  }

  return defaultParams;
}
